import { Box, Chip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type {
  CreativeColumnKey,
  HypothesisCreativeEntry,
} from '../../types/hypothesis';
import CreativePreviewCell from './CreativePreviewCell';

const getTpcDisplay = (creative: HypothesisCreativeEntry): string => {
  if (creative.tpc_model === 'tpc' && creative.tpc_finish_date) {
    return creative.tpc_finish_date;
  }
  if (creative.tpc_model === 'non-tpc' || creative.tpc_model === 'half-tpc') {
    return creative.tpc_model;
  }
  return '-';
};

export const ALL_COLUMN_DEFS: Record<CreativeColumnKey, GridColDef> = {
  dragHandle: {
    field: '__dragHandle',
    headerName: '',
    width: 40,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    resizable: false,
    renderCell: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          color: 'text.disabled',
          cursor: 'grab',
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
    ),
  },
  preview: {
    field: 'preview_link',
    headerName: 'PREVIEW',
    width: 60,
    sortable: false,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <CreativePreviewCell
        previewLink={params.row.preview_link ?? params.row.thumbnail_url ?? params.row.url}
        name={params.row.name}
      />
    ),
  },
  name: {
    field: 'name',
    headerName: 'NAME',
    flex: 1,
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          height: '100%',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {params.row.name}
        </Typography>
      </Box>
    ),
  },
  flow: {
    field: 'flow',
    headerName: 'FLOW',
    width: 120,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2">{params.row.flow ?? '-'}</Typography>
    ),
  },
  customer: {
    field: 'customer',
    headerName: 'CUSTOMER',
    width: 120,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2">{params.row.customer ?? '-'}</Typography>
    ),
  },
  tpc: {
    field: 'tpc_finish_date',
    headerName: 'TPC FINISH DATE',
    width: 140,
    valueGetter: (_value: unknown, row: HypothesisCreativeEntry) => getTpcDisplay(row),
  },
  creative_type: {
    field: 'creative_type',
    headerName: 'TYPE',
    width: 90,
  },
  concept: {
    field: 'concept',
    headerName: 'CONCEPT',
    width: 120,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2">{params.row.concept ?? '-'}</Typography>
    ),
  },
  variable: {
    field: 'variable',
    headerName: 'VARIABLE',
    width: 120,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2">{params.row.variable ?? '-'}</Typography>
    ),
  },
  idea_type: {
    field: 'idea_type',
    headerName: 'IDEA TYPE',
    width: 120,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2">{params.row.idea_type ?? '-'}</Typography>
    ),
  },
  headline: {
    field: 'headline',
    headerName: 'HEADLINE',
    width: 150,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2" noWrap>{params.row.headline ?? '-'}</Typography>
    ),
  },
  technology: {
    field: 'technology',
    headerName: 'TECHNOLOGY',
    width: 120,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
      <Typography variant="body2">{params.row.technology ?? '-'}</Typography>
    ),
  },
  uploaded: {
    field: 'uploaded',
    headerName: 'UPLOADED',
    width: 120,
  },
  status: {
    field: 'launch_status',
    headerName: 'STATUS',
    width: 130,
    renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => {
      if (params.row.launch_status === 'launched') {
        return (
          <Chip
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            label="Launched"
            size="small"
            sx={{
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              fontWeight: 500,
              '& .MuiChip-icon': { color: '#2e7d32' },
            }}
          />
        );
      }
      return (
        <Typography variant="body2" color="text.secondary">
          Not launched
        </Typography>
      );
    },
  },
};
